import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { authApi } from '@/services/authService';
import {
  Search, Plus, Users, ChevronLeft, ChevronRight,
  Mail, ArrowUpDown, DollarSign, Target, Handshake
} from "lucide-react";
import { toast } from "sonner";

import { Header } from '@/components/layout/header/Header';

function useDebounce<T>(value: T, delay: number): T {
  useLanguage();
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

interface NetworkUser {
  id_user: string;
  nome: string;
  sobrenome: string;
  email: string;
  nivel: 'customer' | 'seller' | 'manager' | 'admin' | 'platform' | 'support';
  status: string;
  createdAt: string;
  totalCommissionsPending: number;
  totalCommissionsPaid: number;
  lastStripeStatus: string;
  purchased_plan: string;
  plano: string;
  plano_preco: string;
  paymentForecast?: string;
}

interface ReferralLink {
  label: string;
  url: string;
}
interface Summary {
  "grandTotalPending": number,
  "grandTotalPaid": number,
  "totalSellersActivesOnPlatform": number,
  "totalManagersActivesOnPlatform": number,
  "totalUsersActivesOnPlatform": number
}

export default function NetworkList() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<NetworkUser[]>([]);
  const [referralLinks, setReferralLinks] = useState<ReferralLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, currentPage: 1 });
  const [summaryy, setSummary] = useState<Summary>();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: '',
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  useEffect(() => {
    if (user && user.nivel === 'customer') {
      toast.error(translateInterfaceText("Área restrita a parceiros e gestores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchReferralLinks = useCallback(async () => {
    if (!user || !['seller', 'manager', 'admin'].includes(user.nivel)) return;
    try {
      const response = await authApi.get('/stripe/afiliate/finance/my-links');
      console.log(response.data)
      if (response.data.success) {
        setReferralLinks(response.data.links);
      }
    } catch (err) {
      console.error("Erro ao buscar links de indicação:", err);
    }
  }, [user]);
  const fetchData = useCallback(async () => {
    if (!user || user.nivel === 'customer') return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        search: debouncedSearch,
        status: filters.status,
        page: filters.page.toString(),
        limit: filters.limit.toString(),
        orderBy: filters.orderBy,
        orderDirection: filters.orderDirection
      });
      const response = await authApi.get(`/stripe/afiliate/finance/user/network/list?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
      setSummary(response.data.summary);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao carregar dados da rede."));
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch, filters]);

  useEffect(() => {
    fetchData();
    fetchReferralLinks();
  }, [fetchData, fetchReferralLinks]);

  const handleCopyLink = (labelType: string) => {
    const linkObj = referralLinks.find(l => l.label.toLowerCase().includes(labelType.toLowerCase()));
    if (!linkObj) {
      toast.error(translateInterfaceText("Link de {value0} não encontrado.", { value0: (labelType) }));
      return;
    }
    navigator.clipboard.writeText(linkObj.url);
    toast.success(translateInterfaceText("Link copiado!"), {
      description: translateInterfaceText("O link para {value0} já está na sua área de transferência.", { value0: (linkObj.label) }),
      duration: 3000,
    });
  };

  const handleSort = (column: string) => {
    const isSame = filters.orderBy === column;
    const direction = isSame && filters.orderDirection === 'ASC' ? 'DESC' : 'ASC';
    setFilters(prev => ({ ...prev, page: 1, orderBy: column, orderDirection: direction }));
  };

  if (!user || user.nivel === 'customer') return null;

  const totalActiveClients = users.filter(u => u.status === 'A' && u.plano).length;
  const totalActiveSellers = users.filter(u => u.status === 'A' && ['seller', 'manager'].includes(u.nivel)).length;
  const totalCommissionsReceived = summaryy?.grandTotalPaid ?? 0;
  const formattedCommissions = totalCommissionsReceived.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });

  return (
    <Layout fixedSidebar={true}>
      <div className="flex-1 flex flex-col bg-background lg:overflow-hidden mobile-scroll-fix">
        <Header
          title={translateInterfaceText("Minha Rede")}
          icon={<Users className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />}
        />

        {/* Divisor horizontal padrão HostVSL */}
        <div className="hidden lg:block h-px bg-border w-full" />

        <main className="flex-1 lg:overflow-y-auto scrollbar-blue">
          <div className="p-8 max-w-7xl mx-auto space-y-8 font-sans antialiased text-slate-800">
            {/* HEADER COM BOTÕES ALINHADOS À DIREITA */}
            <div className="flex flex-wrap gap-3 justify-end">
              {['seller', 'manager', 'admin'].includes(user.nivel) && (
                <>
                  <button
                    onClick={() => handleCopyLink('Cliente')}
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-xs font-semibold shadow-sm transition-all active:scale-95"
                  >
                    <Plus size={16} /> {" "}{translateInterfaceText("Convidar Cliente")}</button>

                  {['seller', 'admin'].includes(user.nivel) && (
                    <button
                      onClick={() => handleCopyLink('Vendedor')}
                      className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-xs font-semibold shadow-sm transition-all active:scale-95"
                    >
                      <Plus size={16} /> {" "}{translateInterfaceText("Convidar Parceiro")}</button>
                  )}
                </>
              )}
            </div>

            {/* CARDS DE MÉTRICAS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-full shrink-0">
                  <Users size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 font-medium">{translateInterfaceText("Total de Indicados")}</p>
                  <p className="text-2xl font-bold text-slate-900">{pagination.total}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{translateInterfaceText("Pessoas cadastradas através dos seus links.")}</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-full shrink-0">
                  <DollarSign size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 font-medium">{translateInterfaceText("Comissões Recebidas")}</p>
                  <p className="text-2xl font-bold text-slate-900">{formattedCommissions}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{translateInterfaceText("Total recebido através da sua rede.")}</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-full shrink-0">
                  <Target size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 font-medium">{translateInterfaceText("Clientes Ativos")}</p>
                  <p className="text-2xl font-bold text-slate-900">{totalActiveClients}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{translateInterfaceText("Clientes que assinaram através do seu link.")}</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-full shrink-0">
                  <Handshake size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 font-medium">{translateInterfaceText("Vendedores Ativos")}</p>
                  <p className="text-2xl font-bold text-slate-900">{totalActiveSellers}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{translateInterfaceText("Vendedores indicados que já geraram comissões.")}</p>
                </div>
              </div>
            </div>

            {/* COMO GANHAR COM SUA REDE */}
            {['seller', 'manager', 'admin'].includes(user.nivel) && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div>
                  <h2 className="text-md font-bold text-slate-900">{translateInterfaceText("Como Ganhar Com Sua Rede")}</h2>
                  <p className="text-xs text-slate-400 mt-1">{translateInterfaceText("Convide clientes e vendedores para a Host VSL e receba comissões automáticas sempre que sua rede gerar resultados.")}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-blue-100 text-blue-600 rounded-full shrink-0">
                        <DollarSign size={18} />
                      </div>
                      <p className="text-sm font-bold text-slate-900 flex-1">{translateInterfaceText("Indique Clientes")}</p>
                      <span className="bg-blue-50 text-blue-600 rounded-full px-2.5 py-1 text-[10px] font-bold whitespace-nowrap">{translateInterfaceText("25% recorrente")}</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{translateInterfaceText("Receba 25% todos os meses enquanto o cliente permanecer ativo na plataforma.")}</p>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1.5">
                      <p className="text-[11px] font-medium text-slate-600">{translateInterfaceText("Cliente assinou um plano de R$ 100/mês")}</p>
                      <p className="text-xs font-bold text-slate-800">{translateInterfaceText("Você recebe")}{" "}<span className="text-blue-600">{translateInterfaceText("R$ 25/mês")}</span></p>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-blue-100 text-blue-600 rounded-full shrink-0">
                        <Handshake size={18} />
                      </div>
                      <p className="text-sm font-bold text-slate-900 flex-1">{translateInterfaceText("Indique Parceiros")}</p>
                      <span className="bg-blue-50 text-blue-600 rounded-full px-2.5 py-1 text-[10px] font-bold whitespace-nowrap">{translateInterfaceText("10% das comissões deles")}</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{translateInterfaceText("Receba 10% sobre todas as comissões geradas pelos vendedores indicados diretamente por você.")}</p>
                    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1.5">
                      <p className="text-[11px] font-medium text-slate-600">{translateInterfaceText("Vendedor gerou R$ 10.000 em comissões")}</p>
                      <p className="text-xs font-bold text-slate-800">{translateInterfaceText("Você recebe")}{" "}<span className="text-blue-600">R$ 1.000</span></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TABELA E ELEMENTOS DE FILTRO */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
              <div>
                <h2 className="text-md font-bold text-slate-900">{translateInterfaceText("Membros da Rede")}</h2>
                <p className="text-xs text-slate-400 mt-0.5">{translateInterfaceText("Todos que se cadastraram através dos seus links.")}</p>
              </div>

              <div className="flex flex-col md:flex-row gap-3 items-center">
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                  <input
                    type="text"
                    placeholder={translateInterfaceText("Buscar por nome ou email...")}
                    className="w-full pl-11 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none transition-all placeholder:text-slate-300 focus:border-blue-500/40 focus:ring-4 focus:ring-blue-500/5"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="flex gap-2 w-full md:w-auto shrink-0">
                  <select
                    className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none cursor-pointer hover:bg-slate-50 transition-colors"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                  >
                    <option value="">{translateInterfaceText("Todos Status")}</option>
                    <option value="active">{translateInterfaceText("Ativos")}</option>
                    <option value="inactive">{translateInterfaceText("Inativos")}</option>
                  </select>

                  <select
                    className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none cursor-pointer hover:bg-slate-50 transition-colors"
                    value={filters.limit}
                    onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value), page: 1 })}
                  >
                    <option value={10}>{translateInterfaceText("10 p/ pág")}</option>
                    <option value={20}>{translateInterfaceText("20 p/ pág")}</option>
                    <option value={50}>{translateInterfaceText("50 p/ pág")}</option>
                    <option value={100}>{translateInterfaceText("100 p/ pág")}</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        <th onClick={() => handleSort('nome')} className="px-6 py-4 cursor-pointer hover:text-blue-600 select-none transition-colors">
                          <div className="flex items-center gap-1.5">{translateInterfaceText("Usuário")}{" "}<ArrowUpDown size={12} className="opacity-50" /></div>
                        </th>
                        <th onClick={() => handleSort('status')} className="px-6 py-4 cursor-pointer hover:text-blue-600 select-none transition-colors">
                          <div className="flex items-center gap-1.5">{translateInterfaceText("Status")}{" "}<ArrowUpDown size={12} className="opacity-50" /></div>
                        </th>
                        <th onClick={() => handleSort('purchased_plan')} className="px-6 py-4 cursor-pointer hover:text-blue-600 select-none transition-colors">
                          <div className="flex items-center gap-1.5">{translateInterfaceText("Plano / Valor")}{" "}<ArrowUpDown size={12} className="opacity-50" /></div>
                        </th>
                        <th onClick={() => handleSort('createdAt')} className="px-6 py-4 cursor-pointer hover:text-blue-600 select-none transition-colors">
                          <div className="flex items-center gap-1.5">{translateInterfaceText("Cadastro")}{" "}<ArrowUpDown size={12} className="opacity-50" /></div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-10 text-center text-xs text-slate-400 font-medium">
                            {translateInterfaceText("Carregando registros da rede...")}</td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-10 text-center text-xs text-slate-400 font-medium">
                            {translateInterfaceText("Nenhum membro encontrado na sua rede.")}</td>
                        </tr>
                      ) : (
                        users.map((u) => (
                          <tr key={u.id_user} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-slate-50 border border-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold uppercase shrink-0">
                                  {u.nome.charAt(0)}
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-slate-900">{u.nome} {u.sobrenome}</p>
                                  <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><Mail size={11} /> {u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${u.status === 'A' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                {u.status === 'A' ? translateInterfaceText("Ativo") : translateInterfaceText("Bloqueado")}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="text-xs font-semibold text-slate-700">{u.plano || translateInterfaceText("Sem Plano")}</span>
                                <span className="text-[11px] text-slate-400">{u.plano_preco || '---'}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs font-medium text-slate-500">
                              {new Date(u.createdAt).toLocaleDateString(getInterfaceLanguage())}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                </table>
              </div>

              {/* PAGINAÇÃO */}
              <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                  {translateInterfaceText("Total de")}{" "}{pagination.total} {" "}{translateInterfaceText("registros")}</p>

                <div className="flex items-center gap-2">
                  <button
                    disabled={filters.page === 1}
                    onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-400 disabled:opacity-30 hover:bg-slate-50 transition-all"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="h-7 w-7 flex items-center justify-center bg-blue-600 rounded-lg text-xs font-bold text-white shadow-sm">
                    {filters.page}
                  </div>

                  <button
                    disabled={filters.page === pagination.pages}
                    onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-400 disabled:opacity-30 hover:bg-slate-50 transition-all"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
}